import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ScheduleType } from '@cornerstone/shared';
import { API_BASE } from '../client/config';
import { api } from '../client/api';
import { useDeviceGroupsStore, type ApprovalMode, type DeviceGroup, type GroupDevice } from '../store/deviceGroups';
import { Button, CtrlLabel, Panel, SearchInput } from '../components/ui';
import { DangerZone } from '../components/DangerZone';
import { GroupScopeSetup } from '../components/GroupScopeSetup';
import { NO_GROUP_TITLES, NoAssignedGroupsNotice, hasNoAssignedGroups } from '../components/GroupAssignmentNotice';
import { AdminGroupSection, adminAccessFields } from '../components/AdminGroupSection';
import { ConfirmModal, InfoTooltip } from '../components/menu';
import { ContentSkeleton } from '../components/skeleton/ContentSkeleton';
import { useDeviceDiagnostics, type DiagnosticStatus } from '../components/DeviceDiagnostics';
import { DeviceCodeClaimButton } from '../components/DeviceCodeClaim';
import { NewDevicePairing, canPair, pairingGroups } from '../components/DevicePairing';
import { DeviceDetailModal } from '../components/DeviceDetailModal';
import { StorageDetailModal } from '../components/StorageUsagePanel';
import type { GroupMemberAssignments } from '../components/GroupMemberEditor';
import { describeApiError, toast } from '../components/toast';
import { APPROVAL_MODE_LABELS, describeGroupScope, roleLabel } from '../labels/orgConcepts';
import { useWriteGate, type WriteGate } from '../nav/useWriteGate';
import { AdminFormModal, reportAdminSaveResult, type AdminRow } from './bo/OrgAdmin';
import {
  ACTION_CELL_CLASS, ACTION_HEAD_CLASS,
  AllGroupsIcon, DeviceRowView, GroupFormModal, GroupMark, GroupMenu, SELECT_CLASS, STATUS_TOOLTIP, SelectableRow,
  allowedGroupRoles, canManageDevices, type GroupMemberCandidate, type GroupMemberRole,
} from './DeviceGroups';
import { cn } from '../utils/cn';
import { useIsMobile } from '../utils/useIsMobile';

// =============================================================================
// 구성 관리 — 그룹 · 관리자 · 디바이스를 한 화면에서 (2026-09-15, 대교 결정). 탭 없는 단일 메뉴.
//
// 관제 콘솔에서 익숙한 "왼쪽 대상 목록 + 오른쪽 상세" 마스터-디테일이다. 왼쪽은 그룹(전체 · 각 그룹 · 미배정),
// 오른씽은 고른 대상의 요약 → 관리자 → 디바이스 순으로 위에서 아래로 읽힌다.
//   • 전체    = 조직 대시보드: 지표 카드(그룹·디바이스·관리자·저장 공간) → 주의 필요(미배정·장애·그룹 없는 관리자,
//              누르면 그 자리로) → 그룹 권한 상태 → 관리자 표(담당 범위) → 디바이스 표(전체, 상태 필터).
//   • 그룹    = 머리(승인 방식·기기·관리자) + [그룹 수정] → 관리자 표(권한·수정·삭제) → 디바이스 표.
//   • 역할은 '권한' 하나. 그룹별 역할은 두지 않는다 — 담당 그룹은 어느 그룹인지만 정하고, 그 안에서 권한대로 활동한다.
//   • 목록    = Owner·중앙: 전체·각 그룹·미배정. 그룹 관리자: 담당 그룹만, 둘 이상일 때만 '전체'. 하나면 그 그룹이 바로 열림.
//   • 관리자 수정 모달의 '담당 그룹'에서 그룹·역할을 바꾸고 체크를 풀어 빼낸다(별도 '제외' 버튼 없음).
//   • 미배정  = 그룹 없는 기기(그룹 이동) + 그룹이 없는 관리자.
// 용어는 '관리자' 하나만 쓴다(담당자 ✗). 그룹에 관리자를 넣는 자리는 두 곳: 그룹 수정 모달의 '관리자' 섹션,
// 그리고 관리자 추가 모달의 '담당 그룹' 섹션(새 계정을 만들면서 바로 배정).
// 데이터: /api/device-groups(그룹·기기·actor) + /api/orgs/current/storage(저장 한도·콘텐츠 사용량, 서버 디스크는 운영사만)
// + /api/device-groups/member-assignments(관리자 명단·그룹 배정). 관리자 표의 출처는 역할에 따라 갈린다 —
//   조직 Owner는 /api/orgs/current/detail(org:manage), 그룹 관리자는 명단 API가 담당 그룹으로 깎아 준 것
//   (2026-09-17 대교: "그룹의 관리자들도 볼 수 있게"). 넣고 빼는 건 그대로 조직 Owner뿐이다.
// =============================================================================

type OrgDetail = { org: { id: string; name: string }; admins: AdminRow[] };
/** 저장 공간 카드 — 저장 한도 대비 콘텐츠 사용량(원본·승인본·백업 등). 서버 디스크는 운영사만 본다(2026-10-12 대교). */
type Usage = { usedBytes: number; quotaBytes: number | null };
type Selection = 'all' | 'unassigned' | string;
type DeviceSortKey = 'status' | 'name' | 'group';
const STATUS_ORDER: Record<DiagnosticStatus, number> = { fault: 0, offline: 1, online: 2 };
const REFRESH_MS = 15_000;
const ADMINS_BASE = `${API_BASE}/api/orgs/current/admins`;

function formatDateTime(iso: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}. ${p(d.getMonth() + 1)}. ${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
function formatBytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}
const adminLabel = (member: { name: string | null; loginId: string | null; email: string }) => member.name ?? member.loginId ?? member.email;
/** 표에서 읽기 쉬운 상대 시간("3일 전"). 정확한 시각은 title로. */
function relativeTime(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}일 전`;
  return formatDateTime(iso).slice(0, 13);
}

export function GroupWorkspace(): JSX.Element {
  const store = useDeviceGroupsStore;
  const { groups, devices, actor, loading, pending, error, load, createGroup, updateGroup, deleteGroup, assignDevice } = store();
  const [detail, setDetail] = useState<OrgDetail | null>(null);
  /** 관리자 목록·배정은 조직 Owner(·운영사)의 몫. 서버가 403을 주면 그 패널을 아예 그리지 않는다(오류 아님). */
  const [adminsDenied, setAdminsDenied] = useState(false);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [storageOpen, setStorageOpen] = useState(false);
  const [assignments, setAssignments] = useState<GroupMemberAssignments[] | null>(null);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [selection, setSelection] = useState<Selection>('all');
  // 모바일 — 그룹 목록과 상세를 한 화면씩. 목록에서 고르면 상세로 넘어가고 ‹ 그룹 목록으로 돌아온다(2026-09-23).
  const isMobile = useIsMobile();
  const [mobileDetail, setMobileDetail] = useState(false);
  const choose = (next: Selection) => { setSelection(next); setMobileDetail(true); };
  const [createOpen, setCreateOpen] = useState(false);
  // 기기 추가를 누르려다 그룹이 없어 그룹부터 만드는 중이면, 만든 뒤 바로 기기 추가를 연다(SCDS-70).
  const [groupFirstPrompt, setGroupFirstPrompt] = useState(false);
  const [pairAfterGroup, setPairAfterGroup] = useState(false);
  const [editTarget, setEditTarget] = useState<DeviceGroup | null>(null);
  const [memberDraft, setMemberDraft] = useState<Map<string, GroupMemberRole>>(new Map());
  const [adminModal, setAdminModal] = useState<{ mode: 'add' | 'edit'; admin?: AdminRow; presetGroup?: string } | null>(null);
  /** 관리자 추가 모달의 담당 그룹(역할은 저장 뒤 조직 권한으로 정해짐). */
  const [newAdminGroups, setNewAdminGroups] = useState<Set<string>>(new Set());
  /** 관리자 수정 모달의 담당 그룹. 그룹 안 역할은 따로 없다 — 위의 '권한'이 곧 그룹에서의 역할(대교 결정 2026-09-15). */
  const [adminGroupDraft, setAdminGroupDraft] = useState<Set<string>>(new Set());
  /** 중앙 관리자(모든 그룹을 관리) 지정 — 그룹 관리자(Admin) 권한에서만 뜻이 있다. 추가·수정 모달 공용. */
  const [centralDraft, setCentralDraft] = useState(false);
  const [deleteAdmin, setDeleteAdmin] = useState<AdminRow | null>(null);
  const [pairingOpen, setPairingOpen] = useState(false);
  const [deviceDetail, setDeviceDetail] = useState<{ id: string; mode: 'view' | 'edit'; isNew?: boolean } | null>(null);
  const [timelines, setTimelines] = useState<ScheduleType[]>([]);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | DiagnosticStatus>('');
  /** 디바이스 표 정렬(S5). 기본은 상태(장애→오프라인→온라인) — 문제 있는 기기가 위로. */
  const [sort, setSort] = useState<{ key: DeviceSortKey; dir: 'asc' | 'desc' }>({ key: 'status', dir: 'asc' });
  const toggleSort = (key: DeviceSortKey) => setSort((current) => current.key === key ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const initialLoading = !actor && !error;
  const busy = initialLoading || loading || pending || saving;
  const canManageGroups = !!actor?.central && (actor.role === 'Owner' || actor.role === 'Admin');
  const isOwner = !!actor?.central && actor.role === 'Owner';
  // 목록 규칙(대교): Owner·중앙은 전체·각 그룹·미배정. 그룹 관리자는 담당 그룹만 — 둘 이상일 때만 '전체'가 의미 있다.
  const showAllRow = !!actor && (actor.central || !actor.enforced || actor.groups.length > 1);
  const showUnassignedRow = !!actor && (actor.central || !actor.enforced);
  const canManageMembers = isOwner && !adminsDenied;
  const canManageAnyDevice = !!actor && (canManageDevices(actor, null) || actor.groups.some((grant) => canManageDevices(actor, grant.groupId)));
  const scope = actor ? { actor, groups, devices } : null;
  const pairable = !!scope && canPair(scope);
  // 그룹 운영 중인 조직에서 기기는 반드시 그룹에 연결된다. 그룹이 하나도 없으면 페어링할 곳이
  // 없어 버튼이 통째로 사라졌다(SCDS-70). 그룹을 만들 수 있는 관리자에겐 버튼을 두고,
  // 누르면 그룹부터 만들게 안내한 뒤 이어서 기기 추가를 연다.
  const needsGroupFirst = !pairable && !!actor?.enforced && canManageGroups && groups.length === 0;

  // 쓰기 컨트롤 게이트. 대행 '읽기 전용' 같은 일시적 차단은 비활성 + 이유로 남기고,
  // 역할상 영구히 불가한 조작은 아예 숨긴다(hide) — 눌러 볼 수도 없는 버튼은 두지 않는다.
  const write = useWriteGate();
  const groupWrite = write.and(canManageGroups, '조직 관리자·중앙 관리자만 그룹을 만들거나 고칠 수 있습니다.', { hide: true });
  const memberWrite = write.and(canManageMembers, '조직 관리자만 관리자를 추가·수정·삭제할 수 있습니다.', { hide: true });
  const deviceWrite = write
    .and(canManageAnyDevice, '기기를 관리할 수 있는 관리자 권한이 없습니다.', { hide: true })
    .and(pairable || needsGroupFirst, '기기를 추가할 수 있는 관리자 권한의 그룹이 없습니다.', { hide: true });

  const loadAdmins = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/orgs/current/detail`, { credentials: 'include' });
      if (response.status === 401 || response.status === 403) { setAdminsDenied(true); setDetail(null); return; }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setAdminsDenied(false);
      setDetail(await response.json() as OrgDetail);
      setAdminError(null);
    } catch (cause) { setAdminError(describeApiError(cause)); }
  }, []);
  const loadUsage = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/orgs/current/storage`, { credentials: 'include' });
      if (response.ok) setUsage(await response.json() as Usage);
    } catch { /* 저장 공간은 부가 정보 — 실패해도 화면을 막지 않는다. */ }
  }, []);
  /** 관리자 명단 + 그룹 배정. 조직 Owner는 조직 전체, 그룹 관리자는 담당 그룹 사람만 받는다
   *  (서버가 깎아 준다). 볼 자격이 없으면 403 — 오류가 아니라 '명단 패널 없음'이다. */
  const loadAssignments = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/device-groups/member-assignments`, { credentials: 'include' });
      if (response.status === 401 || response.status === 403) { setAssignments(null); return; }
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      setAssignments(await response.json() as GroupMemberAssignments[]);
    } catch (cause) { setAdminError(describeApiError(cause)); }
  }, []);
  const reloadAll = useCallback(async () => { await Promise.all([load(), isOwner ? loadAdmins() : Promise.resolve(), loadAssignments(), loadUsage()]); }, [load, loadAdmins, loadAssignments, loadUsage, isOwner]);

  useEffect(() => { void load(); void loadUsage(); }, [load, loadUsage]);
  useEffect(() => { if (isOwner) void loadAdmins(); }, [isOwner, loadAdmins]);
  useEffect(() => { void loadAssignments(); }, [loadAssignments]);
  useEffect(() => {
    const timer = setInterval(() => { if (!document.hidden) void store.getState().refresh(); }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [store]);
  useEffect(() => { void api.listScheduleTypes().then(setTimelines).catch(() => setTimelines([])); }, [devices.length]);
  useEffect(() => {
    if (selection !== 'all' && selection !== 'unassigned' && !loading && actor && !groups.some((group) => group.id === selection)) setSelection('all');
  }, [selection, groups, loading, actor]);
  // '전체' 행이 없는 역할(그룹 하나만 맡은 관리자)은 그 그룹을 바로 연다.
  useEffect(() => {
    if (!actor || showAllRow || groups.length === 0) return;
    setSelection((current) => current === 'all' || current === 'unassigned' ? groups[0]!.id : current);
  }, [actor, groups, showAllRow]);

  const diagnostics = useDeviceDiagnostics(devices.map((device) => device.id));
  const statusOf = (device: GroupDevice): DiagnosticStatus => diagnostics.data[device.id]?.status ?? (device.online ? 'online' : 'offline');
  const countBy = (list: GroupDevice[], status: DiagnosticStatus) => list.filter((device) => statusOf(device) === status).length;
  const timelinesByDevice = useMemo(() => {
    const map = new Map<string, ScheduleType[]>();
    for (const type of timelines) { if (type.deviceId) map.set(type.deviceId, [...(map.get(type.deviceId) ?? []), type]); }
    return map;
  }, [timelines]);
  const assignmentOf = useMemo(() => new Map((assignments ?? []).map((entry) => [entry.userId, entry])), [assignments]);
  /** 관리자 표의 행. 조직 Owner는 조직 상세(/api/orgs/current/detail)에서, 그룹 관리자는 담당 그룹으로
   *  깎인 명단 API에서 온다 — 그래서 그룹 관리자에게는 자기 그룹 사람만 보인다(2026-09-17 대교). */
  const admins: AdminRow[] = useMemo(() => detail?.admins ?? (assignments ?? []).map((entry) => ({
    userId: entry.userId, name: entry.name ?? null, loginId: entry.loginId ?? null, phone: null,
    email: entry.email ?? '', role: entry.role, lastSeenAt: entry.lastSeenAt ?? null,
  })), [detail, assignments]);
  /** 명단을 하나라도 받았으면 보여 준다. 고칠 수 있는지는 memberWrite가 따로 판단한다. */
  const showAdmins = canManageMembers || admins.length > 0;
  const groupName = useMemo(() => new Map(groups.map((group) => [group.id, group.name])), [groups]);
  /** 그룹 → 관리자(조직 관리자 행 + 이 그룹 역할). 조직 관리자·중앙 관리자는 모든 그룹을 보는 사람이라 따로 표시한다. */
  const adminsOf = (groupId: string) => admins.flatMap((admin) => {
    const role = assignmentOf.get(admin.userId)?.assignments.find((entry) => entry.groupId === groupId)?.role;
    return role ? [{ admin, role }] : [];
  });
  const centralAdmins = admins.filter((admin) => admin.role === 'Owner' || assignmentOf.get(admin.userId)?.central);
  const unassignedAdmins = admins.filter((admin) => admin.role !== 'Owner' && !assignmentOf.get(admin.userId)?.central && (assignmentOf.get(admin.userId)?.assignments.length ?? 0) === 0);
  const unassignedDevices = useMemo(() => devices.filter((device) => !device.groupId), [devices]);
  const faultDevices = devices.filter((device) => statusOf(device) === 'fault');
  const selectedGroup = selection !== 'all' && selection !== 'unassigned' ? groups.find((group) => group.id === selection) ?? null : null;
  const candidates: GroupMemberCandidate[] = admins.map((admin) => ({
    userId: admin.userId, name: admin.name, loginId: admin.loginId, email: admin.email, role: admin.role, central: !!assignmentOf.get(admin.userId)?.central,
  }));
  const collator = useMemo(() => new Intl.Collator('ko'), []);
  const filterDevices = (list: GroupDevice[]) => {
    const q = query.trim().toLowerCase();
    const sign = sort.dir === 'asc' ? 1 : -1;
    return list
      .filter((device) => (!q || device.name.toLowerCase().includes(q) || device.id.toLowerCase().includes(q)) && (!statusFilter || statusOf(device) === statusFilter))
      .sort((a, b) => {
        const primary = sort.key === 'status' ? STATUS_ORDER[statusOf(a)] - STATUS_ORDER[statusOf(b)]
          : sort.key === 'group' ? collator.compare(groupName.get(a.groupId ?? '') ?? '힣', groupName.get(b.groupId ?? '') ?? '힣')
          : collator.compare(a.name, b.name);
        return (primary || collator.compare(a.name, b.name)) * sign;
      });
  };

  /** 한 사람의 그룹 배정을 통째로 바꾼다(다른 그룹 배정 유지). role=null이면 그 그룹에서 제외. */
  async function putAssignment(userId: string, changes: Array<{ groupId: string; role: GroupMemberRole | null }>, central?: boolean): Promise<void> {
    const current = assignmentOf.get(userId);
    // 그룹 역할은 사람이 고르는 값이 아니라 이 사람의 조직 권한이다(대교 2026-09-15·2026-09-17).
    // 손대지 않는 다른 그룹의 배정도 같은 값으로 맞춰 보낸다 — 옛 데이터에 조직 권한보다 낮은
    // 역할이 남아 있으면(예: Editor인데 그 그룹에서만 Viewer) 그 그룹에서만 쓰기가 막혀
    // "권한은 편집자인데 왜 안 되냐"가 된다. 저장할 때 조용히 지금 권한으로 정렬한다.
    const orgRole = allowedGroupRoles(admins.find((admin) => admin.userId === userId)?.role ?? '')[0] ?? null;
    let next = [...(current?.assignments ?? [])].map((entry) => ({ ...entry, role: orgRole ?? entry.role }));
    for (const change of changes) {
      next = next.filter((entry) => entry.groupId !== change.groupId);
      if (change.role) next.push({ groupId: change.groupId, role: orgRole ?? change.role });
    }
    const nextCentral = central ?? current?.central ?? false;
    const response = await fetch(`${API_BASE}/api/device-groups/members/${encodeURIComponent(userId)}`, {
      method: 'PUT', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      // 중앙 관리자는 그룹 배정이 뜻이 없다 — 켤 때 배정을 비운다.
      body: JSON.stringify({ assignments: nextCentral ? [] : next, central: nextCentral }),
    });
    if (!response.ok) { const body = await response.json().catch(() => null) as { error?: string } | null; throw new Error(body?.error ?? `HTTP ${response.status}`); }
  }
  /** 담기고/빠진 사람만 저장한다. 역할은 비교에 넣지 않는다 — 어차피 조직 권한으로 보내므로
   *  '그룹 역할이 달라졌다'는 변경은 존재하지 않는다(옛 데이터의 낡은 role은 putAssignment가 정리). */
  async function applyMemberDraft(groupId: string, draft: Map<string, GroupMemberRole>): Promise<void> {
    const before = new Set(adminsOf(groupId).map(({ admin }) => admin.userId));
    const changed = [...new Set([...before, ...draft.keys()])].filter((userId) => before.has(userId) !== draft.has(userId));
    for (const userId of changed) await putAssignment(userId, [{ groupId, role: draft.get(userId) ?? null }]);
    if (changed.length) await loadAssignments();
  }
  function openEdit(group: DeviceGroup): void {
    // 저장된 그룹 역할이 아니라 지금의 조직 권한으로 초기값을 채운다 — 체크 상태만 뜻이 있다.
    setMemberDraft(new Map(adminsOf(group.id).flatMap(({ admin }) => {
      const role = allowedGroupRoles(admin.role)[0];
      return role ? [[admin.userId, role] as const] : [];
    })));
    setEditTarget(group);
  }
  function openEditAdmin(admin: AdminRow): void {
    setAdminGroupDraft(new Set((assignmentOf.get(admin.userId)?.assignments ?? []).map((entry) => entry.groupId)));
    setCentralDraft(!!assignmentOf.get(admin.userId)?.central);
    setAdminModal({ mode: 'edit', admin });
  }
  async function removeAdmin(admin: AdminRow): Promise<void> {
    const response = await fetch(`${ADMINS_BASE}/${encodeURIComponent(admin.userId)}`, { method: 'DELETE', credentials: 'include' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    toast('삭제되었습니다.', 'success');
    await reloadAll();
  }
  function openAddAdmin(presetGroup?: string): void {
    setCentralDraft(false);
    setNewAdminGroups(new Set(presetGroup ? [presetGroup] : []));
    setAdminModal({ mode: 'add', ...(presetGroup ? { presetGroup } : {}) });
  }
  // ⋮ 메뉴는 권한이 없어도 같은 자리에 둔다(S11) — 항목만 비활성 + 이유.
  const menuFor = (group: DeviceGroup) => (
    <GroupMenu group={group} canManage={canManageGroups} write={groupWrite}
      onEdit={() => openEdit(group)}
      onDelete={async (mode) => { if (!await deleteGroup(group.id, mode)) throw new Error(store.getState().error ?? '그룹을 정리하지 못했습니다.'); if (selection === group.id) setSelection('all'); }} />
  );
  const deviceRow = (device: GroupDevice, showGroup: boolean) => (
    <DeviceRowView key={device.id} device={device} groups={groups} actor={actor} busy={busy} status={diagnostics.data[device.id]} online={device.online}
      showGroup={showGroup} timelines={timelinesByDevice.get(device.id) ?? []}
      onOpen={() => setDeviceDetail({ id: device.id, mode: 'view' })} onEdit={() => setDeviceDetail({ id: device.id, mode: 'edit' })}
      onAssign={(groupId) => void assignDevice(device.id, groupId)} />
  );
  // 도구줄은 [검색 + 상태] / [연결 + 추가] 두 덩어리(S14). 1280에서 줄이 바뀌어도 낱개가
  // 아니라 덩어리째 내려간다 — 각 덩어리는 flex-none으로 쪼개지지 않게 둔다.
  const deviceToolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="flex flex-none items-center gap-2">
        <label className="contents">
          <span className="sr-only">디바이스명, ID 검색</span>
          <SearchInput value={query} onChange={setQuery} placeholder="디바이스명, ID 검색" className="w-[200px]" />
        </label>
        <select aria-label="상태 필터" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as '' | DiagnosticStatus)} className={SELECT_CLASS}>
          <option value="">상태 전체</option><option value="online">온라인</option><option value="fault">장애</option><option value="offline">오프라인</option>
        </select>
      </div>
      <div className="flex flex-none items-center gap-2">
        {!deviceWrite.hidden && <>
          <DeviceCodeClaimButton onClaimed={() => void load()} disabled={busy || deviceWrite.blocked || needsGroupFirst}
            {...(deviceWrite.reason ? { disabledReason: deviceWrite.reason } : needsGroupFirst ? { disabledReason: '기기는 그룹에 연결됩니다. 먼저 그룹을 만드세요.' } : {})} />
          <Button variant="primary" {...deviceWrite.props} disabled={busy || deviceWrite.blocked}
            onClick={() => { if (deviceWrite.blocked) return; if (needsGroupFirst) setGroupFirstPrompt(true); else setPairingOpen(true); }}>+ 디바이스 추가</Button>
        </>}
      </div>
    </div>
  );

  // 레이아웃은 플레이리스트 화면과 같은 꼴(대교, 2026-09-15): 왼쪽 목록 패널 + 오른쪽 한 덩어리 섹션.
  // 섹션 머리에 고른 대상의 이름·태그·동작이 있고, 그 아래 요약·관리자·디바이스가 그 대상에 종속된 것처럼 안에서 스크롤된다.
  const headerLabel = selectedGroup ? '그룹 정보' : selection === 'all' ? '조직 전체' : '미배정';
  const headerTitle = selectedGroup ? selectedGroup.name : selection === 'all' ? (detail?.org.name ?? '전체') : '그룹이 없는 기기 · 관리자';
  const groupDevices = selectedGroup ? devices.filter((device) => device.groupId === selectedGroup.id) : [];
  const headerTag = selectedGroup
    ? `${APPROVAL_MODE_LABELS[selectedGroup.approvalMode]} · 기기 ${selectedGroup.deviceCount} (온라인 ${countBy(groupDevices, 'online')}${countBy(groupDevices, 'fault') ? ` · 장애 ${countBy(groupDevices, 'fault')}` : ''}) · 관리자 ${selectedGroup.memberCount}`
    : selection === 'all' ? (showAdmins ? `그룹 ${groups.length} · 기기 ${devices.length} · 관리자 ${admins.length}` : `그룹 ${groups.length} · 기기 ${devices.length}`)
      : (showAdmins ? `기기 ${unassignedDevices.length} · 관리자 ${unassignedAdmins.length}` : `기기 ${unassignedDevices.length}`);

  return (
    <div className={cn('flex min-h-0 flex-col text-text', isMobile ? 'px-3 pb-3 pt-0' : 'h-full px-6 pb-6 pt-4')} aria-busy={busy}>
      {error && <div role="alert" className="mb-4 rounded border border-red/50 bg-red/15 px-3 py-2 text-[15px] text-red">오류: {error}</div>}
      {adminError && <div role="alert" className="mb-4 rounded border border-red/50 bg-red/15 px-3 py-2 text-[15px] text-red">관리자 정보: {adminError}</div>}

      {/* lg(1024px) 미만에선 그룹 패널을 위로 접어 세로 배치(D11) — 우측 표가 300px에 갇히지 않게. */}
      {initialLoading ? <ContentSkeleton variant="table" label="현황을 불러오는 중…" />
        /* 담당 그룹이 하나도 없으면 목록도 상세도 그릴 게 없다. 빈 표 두 개를 보여 주는 대신
           '아직 배정 전'이라고 말하고 다시 확인 버튼을 준다(2026-09-17 대교). 여기가 곧 구성
           관리라 '구성 관리 열기'는 뺀다. */
        : hasNoAssignedGroups(actor) ? <NoAssignedGroupsNotice title={NO_GROUP_TITLES.org} onRetry={() => void reloadAll()} showWorkspaceLink={false} />
        : <fieldset disabled={busy} className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 border-0 p-0 lg:flex-row">
        {/* 왼쪽 — 그룹 목록 패널 */}
        <aside className={cn('flex w-full flex-col min-h-0 rounded-md border border-line bg-panel lg:w-[300px] lg:max-h-none',
          isMobile ? (mobileDetail ? 'hidden' : '') : 'flex-none max-h-[38vh] overflow-hidden')}>
          <header className="flex items-center gap-2 border-b border-line px-3.5 py-3">
            <CtrlLabel>그룹</CtrlLabel>
            <span className="font-mono text-[12px] text-text-dimmer">{groups.length}</span>
            <div className="ml-auto">
              {/* 역할이 안 되면 버튼 자체를 두지 않는다. 대행 읽기 전용은 비활성 + 이유. */}
              {!groupWrite.hidden && <Button variant="primary" {...groupWrite.props} disabled={busy || groupWrite.blocked}
                onClick={() => { setMemberDraft(new Map()); setCreateOpen(true); }}>+ 그룹 만들기</Button>}
            </div>
          </header>
          <ul className={cn('min-h-0 flex-1 divide-y divide-line', !isMobile && 'overflow-auto')} aria-label="그룹 목록">
            {showAllRow && <SelectableRow selected={selection === 'all'} onSelect={() => choose('all')}
              mark={<span aria-hidden="true" className="flex h-8 w-8 flex-none items-center justify-center rounded border border-line text-text-dim"><AllGroupsIcon /></span>}
              title="전체" meta={showAdmins ? `그룹 ${groups.length} · 기기 ${devices.length}대 · 관리자 ${admins.length}명` : `그룹 ${groups.length} · 기기 ${devices.length}대`} />}
            {groups.map((group) => {
              const own = devices.filter((device) => device.groupId === group.id);
              const faults = countBy(own, 'fault');
              return <SelectableRow key={group.id} selected={selection === group.id} onSelect={() => choose(group.id)}
                mark={<GroupMark group={group} />} title={group.name} tag={APPROVAL_MODE_LABELS[group.approvalMode]} badge={faults ? `장애 ${faults}` : undefined}
                meta={`기기 ${group.deviceCount}대 · 온라인 ${countBy(own, 'online')} · 관리자 ${group.memberCount}명`}
                trailing={menuFor(group)} />;
            })}
            {showUnassignedRow && <SelectableRow selected={selection === 'unassigned'} onSelect={() => choose('unassigned')} warn={unassignedDevices.length > 0 || unassignedAdmins.length > 0}
              mark={<span aria-hidden="true" className={cn('flex h-8 w-8 flex-none items-center justify-center rounded border text-[13px]', (unassignedDevices.length > 0 || unassignedAdmins.length > 0) ? 'border-amber-500/60 text-amber-500' : 'border-line text-text-dim')}>{(unassignedDevices.length > 0 || unassignedAdmins.length > 0) ? '!' : '–'}</span>}
              title="미배정" meta={unassignedDevices.length || unassignedAdmins.length ? `기기 ${unassignedDevices.length}대 · 관리자 ${unassignedAdmins.length}명` : '없음'} />}
          </ul>
        </aside>

        {/* 오른쪽 — 고른 대상 한 덩어리: 머리(이름·태그·동작) + 안에서 스크롤되는 본문 */}
        <section className={cn('flex min-h-0 min-w-0 flex-1 flex-col rounded-md border border-line bg-panel', isMobile ? !mobileDetail && 'hidden' : 'overflow-hidden')} aria-label="선택 항목">
          <header className={cn('flex flex-none flex-wrap items-center border-b border-line bg-bg py-3', isMobile ? 'sticky top-0 z-20 gap-2 rounded-t-md px-3' : 'gap-3 px-[22px]')}>
            {isMobile
              ? <button type="button" onClick={() => setMobileDetail(false)}
                  className="-ml-1 inline-flex h-9 items-center rounded px-2 text-[14px] text-text-dim hover:text-text">‹ 그룹 목록</button>
              : <CtrlLabel className="text-accent">{headerLabel}</CtrlLabel>}
            {selectedGroup && <GroupMark group={selectedGroup} />}
            <h2 className="min-w-0 truncate text-[19px] font-semibold tracking-[-0.01em]">{headerTitle}</h2>
            <span className="tk-tag">{headerTag}</span>
            <div className="ml-auto flex items-center gap-2">
              {selectedGroup && !groupWrite.hidden && <Button {...groupWrite.props} disabled={busy || groupWrite.blocked} onClick={() => openEdit(selectedGroup)}>그룹 수정</Button>}
              {selectedGroup && menuFor(selectedGroup)}
              <button type="button" aria-label="새로고침" title="지금 다시 읽기 (15초마다 자동)" disabled={busy} onClick={() => void reloadAll()}
                className="inline-flex h-7 w-7 items-center justify-center rounded-sm border border-line bg-panel text-[15px] text-text-dim hover:border-line-strong hover:text-text disabled:opacity-40">↻</button>
            </div>
          </header>
          {/* 패널은 줄어들지 않고(shrink-0) 이 컬럼이 스크롤한다 — flex 자식 기본 shrink 때문에 표가 잘리던 문제.
              relative: 안쪽 sr-only 라벨(position:absolute)이 이 스크롤 상자를 기준으로 잡혀야 한다. 기준이 없으면
              body에 붙어 스크롤된 만큼 문서 전체가 길어지고, 바깥 페이지가 한 번 더 스크롤됐다(SCDS-71). */}
          <div className={cn('relative flex min-h-0 flex-1 flex-col gap-4 [&>*]:shrink-0', isMobile ? 'p-3' : 'overflow-auto p-[22px]')}>
            {selectedGroup ? <>
              {showAdmins && <Panel title={`관리자 (${adminsOf(selectedGroup.id).length})`} bodyClassName="p-0"
                trailing={memberWrite.hidden ? undefined : <Button variant="primary" {...memberWrite.props} disabled={busy || memberWrite.blocked} onClick={() => openAddAdmin(selectedGroup.id)}>+ 관리자 추가</Button>}>
                {/* 그룹 관리자도 자기 그룹 명단은 본다(2026-09-17 대교). 넣고 빼는 건 memberWrite가 막는다. */}
                <GroupAdminTable rows={adminsOf(selectedGroup.id)} centralAdmins={centralAdmins} write={memberWrite}
                  onEditAdmin={openEditAdmin} />
              </Panel>}
              <DevicePanel title={`디바이스 (${devices.filter((device) => device.groupId === selectedGroup.id).length})`} trailing={deviceToolbar} sort={sort} onSort={toggleSort}
                devices={filterDevices(devices.filter((device) => device.groupId === selectedGroup.id))} showGroup render={(device) => deviceRow(device, true)}
                empty={query || statusFilter ? '검색 결과가 없습니다.' : pairable ? '이 그룹에 배정된 기기가 없습니다. "+ 디바이스 추가"로 바로 연결하세요.' : '이 그룹에 배정된 기기가 없습니다.'} />
            </> : selection === 'all' ? <>
              {(unassignedDevices.length > 0 || unassignedAdmins.length > 0 || faultDevices.length > 0) && (
                /* 스크롤해도 보이게(S1): 본문 스크롤 영역 위에 붙는다. sticky는 스크롤 영역의 패딩 안쪽(content edge)에 붙으므로
                   top을 -22px로 당겨 패딩 위까지 올라가게 하고, 위 22px 패딩과 아래 16px gap(gap-4)까지 바탕으로 덮어 아래 표가 비치지 않게(2026-09-15 대교). */
                <div className="sticky -top-[22px] z-10 -mx-[22px] -mt-[22px] -mb-4 bg-panel px-[22px] pt-[22px] pb-5">
                <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/40 bg-[color-mix(in_srgb,rgb(245_158_11)_6%,var(--color-panel))] px-4 py-2.5 text-[13px]" role="status">
                  <span className="font-medium text-amber-600">주의 필요</span>
                  {unassignedDevices.length > 0 && <button type="button" className="rounded border border-line bg-panel px-2 py-0.5 hover:border-accent" onClick={() => setSelection('unassigned')}>그룹 없는 기기 {unassignedDevices.length}대</button>}
                  {unassignedAdmins.length > 0 && <button type="button" className="rounded border border-line bg-panel px-2 py-0.5 hover:border-accent" onClick={() => setSelection('unassigned')}>그룹 없는 관리자 {unassignedAdmins.length}명</button>}
                  {faultDevices.length > 0 && <button type="button" className="rounded border border-line bg-panel px-2 py-0.5 hover:border-accent" onClick={() => setStatusFilter('fault')}>장애 기기 {faultDevices.length}대</button>}
                </div>
                </div>
              )}
              {/* 지표 — 무엇이 얼마나 있고, 지금 무엇이 문제인지. */}
              <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
                <StatCard label="그룹" value={String(groups.length)} sub={`자동 승인 ${groups.filter((group) => group.approvalMode === 'auto').length} · 승인 필요 ${groups.filter((group) => group.approvalMode !== 'auto').length}`} />
                <StatCard label="디바이스" value={`${devices.length}대`} sub={`온라인 ${countBy(devices, 'online')} · 장애 ${faultDevices.length} · 오프라인 ${countBy(devices, 'offline')}`} warn={faultDevices.length > 0} />
                {showAdmins && <StatCard label="관리자" value={`${admins.length}명`} sub={canManageMembers ? `조직·중앙 ${centralAdmins.length} · 그룹 ${admins.length - centralAdmins.length}` : '담당 그룹 기준'} />}
                <StatCard label="저장 공간" value={usage ? formatBytes(usage.usedBytes) : '-'}
                  sub={usage?.quotaBytes ? `한도 ${formatBytes(usage.quotaBytes)} · ${Math.round((usage.usedBytes / usage.quotaBytes) * 100)}% 사용` : '한도 없음'}
                  warn={!!usage?.quotaBytes && usage.usedBytes / usage.quotaBytes >= 0.8}
                  onOpen={() => setStorageOpen(true)} openLabel="저장 공간 자세히 보기" />
              </div>
              {/* 주의 필요 — 지표가 아니라 할 일. 누르면 그 자리로 간다. */}
              {actor?.central && actor.role === 'Owner' && <GroupScopeSetup orgId={actor.orgId} onApplied={load} />}
              {showAdmins && <Panel title={`관리자 (${admins.length})`} bodyClassName="p-0"
                trailing={memberWrite.hidden ? undefined : <Button variant="primary" {...memberWrite.props} disabled={busy || memberWrite.blocked} onClick={() => openAddAdmin()}>+ 관리자 추가</Button>}>
                <AdminTable admins={admins} write={memberWrite} scopeOf={(admin) => {
                  const current = assignmentOf.get(admin.userId);
                  if (!current) return admin.role === 'Owner' ? '조직 전체' : '-';
                  // 그룹 관리자가 보는 배정은 자기 담당 그룹으로 이미 깎여 있다(서버). 그대로 적는다.
                  return describeGroupScope({ role: admin.role, central: current.central },
                    current.assignments.map((entry) => groupName.get(entry.groupId) ?? '조회 불가 그룹'));
                }} onEdit={openEditAdmin} />
              </Panel>}
              <DevicePanel title={`디바이스 (${devices.length})`} trailing={deviceToolbar} sort={sort} onSort={toggleSort} devices={filterDevices(devices)} showGroup render={(device) => deviceRow(device, true)}
                empty={query || statusFilter ? '검색 결과가 없습니다.' : pairable || needsGroupFirst ? '아직 등록된 디바이스가 없습니다. "+ 디바이스 추가"로 첫 기기를 연결하세요.' : '아직 등록된 디바이스가 없습니다.'} />
            </> : <>
              <DevicePanel title={`그룹 없는 디바이스 (${unassignedDevices.length})`} trailing={deviceToolbar} sort={sort} onSort={toggleSort} devices={filterDevices(unassignedDevices)} showGroup render={(device) => deviceRow(device, true)}
                empty="그룹 없는 기기가 없습니다." />
              {showAdmins && <Panel title={`그룹이 없는 관리자 (${unassignedAdmins.length})`} bodyClassName="p-0">
                {!canManageMembers ? <p className="px-4 py-3 text-[13px] text-text-dim">조직 관리자만 볼 수 있습니다.</p>
                  : unassignedAdmins.length === 0 ? <p className="px-4 py-3 text-[13px] text-text-dim">모든 관리자에게 그룹이 있습니다.</p>
                  : <ul className="divide-y divide-line text-[14px]">{unassignedAdmins.map((admin) => <li key={admin.userId} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="min-w-0 flex-1"><span className="block truncate font-medium">{adminLabel(admin)}</span><span className="block truncate text-[12px] text-text-dim">{admin.email} · {roleLabel(admin.role)}</span></span>
                    {!memberWrite.hidden && <Button aria-label={`${adminLabel(admin)} 수정`} {...memberWrite.props} onClick={() => openEditAdmin(admin)}>수정</Button>}
                  </li>)}</ul>}
              </Panel>}
            </>}
          </div>
        </section>
      </fieldset>}

      {groupFirstPrompt && (
        <ConfirmModal title="먼저 그룹을 만들어 주세요" confirmLabel="그룹 만들기" danger={false}
          message={'디바이스는 그룹에 연결해서 관리합니다. 아직 그룹이 없어 연결할 곳이 없습니다.\n그룹을 만들면 이어서 디바이스 추가 화면이 열립니다.'}
          onCancel={() => setGroupFirstPrompt(false)}
          onConfirm={() => { setGroupFirstPrompt(false); setPairAfterGroup(true); setMemberDraft(new Map()); setCreateOpen(true); }} />
      )}
      {createOpen && canManageGroups && (
        <GroupFormModal mode="create" busy={busy} error={error} canSetPolicy={isOwner}
          members={canManageMembers ? { candidates, value: memberDraft, onChange: setMemberDraft } : undefined}
          onClose={() => { setCreateOpen(false); setPairAfterGroup(false); }}
          onSubmit={async (input) => {
            const ok = await createGroup(input);
            if (!ok) return false;
            // 새 그룹 id는 재조회한 목록에서 이름으로 찾는다(생성 응답을 store가 남기지 않음).
            const created = store.getState().groups.find((group) => group.name === input.name);
            if (created && memberDraft.size) { setSaving(true); try { await applyMemberDraft(created.id, memberDraft); } catch (cause) { setAdminError(describeApiError(cause)); } finally { setSaving(false); } }
            await loadAdmins(); setCreateOpen(false); if (created) setSelection(created.id);
            if (pairAfterGroup) { setPairAfterGroup(false); if (created) setPairingOpen(true); }
            return true;
          }} />
      )}
      {editTarget && canManageGroups && (
        <GroupFormModal mode="edit" group={editTarget} busy={busy} error={error} canSetPolicy={isOwner}
          members={canManageMembers ? { candidates, value: memberDraft, onChange: setMemberDraft } : undefined}
          onClose={() => setEditTarget(null)}
          onSubmit={async (input) => {
            const ok = await updateGroup(editTarget.id, { name: input.name, ...(isOwner ? { approvalMode: input.approvalMode } : {}), policyVersion: editTarget.policyVersion,
              accentColor: input.accentColor ?? null, logoDataUrl: input.logoDataUrl ?? null });
            if (!ok) return false;
            setSaving(true);
            try { await applyMemberDraft(editTarget.id, memberDraft); await load(); await loadAdmins(); }
            catch (cause) { setAdminError(describeApiError(cause)); }
            finally { setSaving(false); }
            setEditTarget(null); return true;
          }} />
      )}
      {adminModal && detail && (
        <AdminFormModal mode={adminModal.mode} adminsBase={ADMINS_BASE} orgName={detail.org.name} {...(adminModal.admin ? { admin: adminModal.admin } : {})}
          access={({ role }) => adminAccessFields({ role, central: centralDraft,
            picked: adminModal.mode === 'add' ? newAdminGroups : adminGroupDraft })}
          extra={({ role }) => (
            <AdminGroupSection role={role} mode={adminModal.mode} groups={groups}
              picked={adminModal.mode === 'add' ? newAdminGroups : adminGroupDraft} central={centralDraft}
              onPick={(next) => { if (adminModal.mode === 'add') setNewAdminGroups(next); else setAdminGroupDraft(next); }}
              onCentral={setCentralDraft} mark={(group) => <GroupMark group={groups.find((g) => g.id === group.id)!} />} />
          )}
          {...(adminModal.mode === 'edit' && adminModal.admin ? { dangerZone: (
            <DangerZone title="위험 구역"
              description={`'${adminLabel(adminModal.admin)}' 계정을 ${detail.org.name} 조직 전체에서 삭제합니다.\n담당하던 모든 그룹에서 사라지고 되돌릴 수 없습니다.`}
              hint="특정 그룹에서만 빼려면 위 '담당 그룹'에서 그 그룹의 체크를 해제하고 저장하세요."
              actionLabel="조직에서 삭제"
              disabled={adminModal.admin.role === 'Owner' || memberWrite.blocked}
              disabledReason={adminModal.admin.role === 'Owner' ? OWNER_DELETE_REASON : memberWrite.reason ?? ''}
              onAction={() => { const target = adminModal.admin!; setAdminModal(null); setDeleteAdmin(target); }} />
          ) } : {})}
          onClose={() => setAdminModal(null)}
          onSaved={async (result) => {
            // 서버가 역할·중앙 여부·담당 그룹을 한 트랜잭션으로 저장하고 결과를 돌려준다.
            // 예전에는 여기서 목록을 다시 읽어 '새로 생긴 사람'을 추론하고 배정을 따로
            // 저장했는데, 그 사이에 실패하면 계정만 남고 담당 그룹이 비었다(R07).
            const wasAdd = adminModal.mode === 'add';
            setAdminModal(null);
            reportAdminSaveResult(result, wasAdd ? '추가되었습니다.' : '저장되었습니다.');
            if (wasAdd && result.role === 'Owner') toast('조직 관리자는 모든 그룹을 관리하므로 담당 그룹을 따로 두지 않습니다.', 'info');
            await reloadAll();
          }} />
      )}
      {deleteAdmin && (
        <ConfirmModal title="조직에서 삭제" confirmLabel="삭제"
          message={`'${adminLabel(deleteAdmin)}' 계정을 조직 전체에서 삭제합니다.\n한 그룹에서만 빼는 것이 아니라 담당하던 모든 그룹에서 사라지며, 되돌릴 수 없습니다.`}
          onCancel={() => setDeleteAdmin(null)}
          onConfirm={async () => { try { await removeAdmin(deleteAdmin); } catch (cause) { setAdminError(describeApiError(cause)); } finally { setDeleteAdmin(null); } }} />
      )}
      {pairingOpen && scope && pairable && (
        <NewDevicePairing key={`${selection}:${groups.length}`} scope={scope}
          initialGroupId={selectedGroup && pairingGroups(scope).some((group) => group.id === selectedGroup.id) ? selectedGroup.id : null}
          onClose={() => setPairingOpen(false)}
          onPaired={(id) => { setPairingOpen(false); void load(); setDeviceDetail({ id, mode: 'edit', isNew: true }); }} />
      )}
      {storageOpen && <StorageDetailModal onClose={() => setStorageOpen(false)} />}
      {deviceDetail && <DeviceDetailModal key={deviceDetail.id} deviceId={deviceDetail.id} initialMode={deviceDetail.mode} isNew={deviceDetail.isNew ?? false}
        groups={groups.map((group) => ({ id: group.id, name: group.name }))} groupId={devices.find((device) => device.id === deviceDetail.id)?.groupId ?? null}
        canEdit={canManageDevices(actor, devices.find((device) => device.id === deviceDetail.id)?.groupId ?? null)}
        onAssignGroup={(groupId) => assignDevice(deviceDetail.id, groupId)}
        onClose={() => setDeviceDetail(null)} onChanged={() => void load()} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// 조각들
// -----------------------------------------------------------------------------

/** 지표 카드. onOpen 을 주면 카드 전체가 자세히 보기 버튼이 된다(저장 공간 — 2026-10-12 대교). */
function StatCard({ label, value, sub, warn = false, onOpen, openLabel }: { label: string; value: string; sub?: string; warn?: boolean; onOpen?: () => void; openLabel?: string }): JSX.Element {
  const body = <>
    <div className="flex items-center justify-between gap-2 text-[12px] text-text-dim">
      <span>{label}</span>
      {onOpen && <span aria-hidden="true" className="inline-flex items-center gap-0.5 text-text-dim group-hover:text-accent">자세히<span className="text-[14px] leading-none">›</span></span>}
    </div>
    <div className="mt-1 text-[20px] font-semibold">{value}</div>
    {sub && <div className={cn('text-[12px]', warn ? 'text-amber-600' : 'text-text-dim')}>{sub}</div>}
  </>;
  const frame = cn('rounded-md border bg-panel px-4 py-3', warn ? 'border-amber-500/50' : 'border-line');
  return onOpen
    ? <button type="button" onClick={onOpen} aria-label={openLabel} className={cn(frame, 'group text-left transition-colors hover:border-accent hover:bg-panel-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring')}>{body}</button>
    : <div className={frame}>{body}</div>;
}

/** 자기 자신(조직 Owner) 행은 지울 수 없다 — 버튼을 없애지 않고 이유를 단다(S11). */
const OWNER_DELETE_REASON = '조직 관리자(Owner) 계정은 이 목록에서 삭제할 수 없습니다.';

function GroupAdminTable({ rows, centralAdmins, write, onEditAdmin }: {
  rows: Array<{ admin: AdminRow; role: string }>;
  centralAdmins: AdminRow[];
  write: WriteGate;
  onEditAdmin: (admin: AdminRow) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table data-mobile-cards className="w-full text-left text-[14px]">
        <thead className="text-[13px] text-text-dim"><tr>
          <th className="px-3 py-2.5 font-medium whitespace-nowrap">이름</th><th className="px-3 py-2.5 font-medium whitespace-nowrap">아이디 · 이메일</th>
          <th className="px-3 py-2.5 font-medium whitespace-nowrap">권한</th>
          <th className="px-3 py-2.5 font-medium whitespace-nowrap">마지막 접속</th><th className={ACTION_HEAD_CLASS} />
        </tr></thead>
        <tbody>
          {rows.map((row) => {
            return <tr key={row.admin.userId} className="group border-t border-line hover:bg-panel-hover">
              <td data-card="full" className="px-3 py-2.5 font-medium">{adminLabel(row.admin)}</td>
              <td className="px-3 py-2.5 text-text-dim"><span className="block truncate">{row.admin.loginId ?? '-'}</span><span className="block truncate text-[12px]">{row.admin.email}</span></td>
              <td className="px-3 py-2.5">{roleLabel(row.admin.role)}</td>
              <td className="px-3 py-2.5 whitespace-nowrap text-text-dim" title={formatDateTime(row.admin.lastSeenAt)}>{relativeTime(row.admin.lastSeenAt)}</td>
              <td className={ACTION_CELL_CLASS}>
                {/* 삭제는 표에 두지 않는다 — 그룹 안 표라서 '이 그룹에서 빼기'로 읽히는데 실제로는
                    조직에서 지운다(2026-09-18 대교). 수정 화면 맨 아래 위험 구역으로 옮겼다. */}
                {!write.hidden && <Button aria-label={`${adminLabel(row.admin)} 수정`} {...write.props} onClick={() => onEditAdmin(row.admin)}>수정</Button>}
              </td>
            </tr>;
          })}
          {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-[14px] text-text-dim">{write.hidden
            ? '이 그룹의 관리자가 없습니다. 관리자를 넣고 빼는 건 조직 관리자가 합니다.'
            : '이 그룹의 관리자가 없습니다. "그룹 수정"에서 지정하거나 "+ 관리자 추가"로 새 계정을 만드세요. 빼는 건 관리자 "수정"의 담당 그룹에서.'}</td></tr>}
          {centralAdmins.length > 0 && <tr><td colSpan={5} className="px-3 py-2 text-[12px] text-text-dimmer">
            이 그룹을 포함해 모든 그룹을 관리: {centralAdmins.map(adminLabel).join(', ')}
          </td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function AdminTable({ admins, write, scopeOf, onEdit }: {
  admins: AdminRow[]; write: WriteGate; scopeOf: (admin: AdminRow) => string;
  onEdit: (admin: AdminRow) => void;
}): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table data-mobile-cards className="w-full text-left text-[14px]">
        <thead className="text-[13px] text-text-dim"><tr>
          <th className="px-3 py-2.5 font-medium whitespace-nowrap">이름</th><th className="px-3 py-2.5 font-medium whitespace-nowrap">아이디 · 이메일</th>
          <th className="px-3 py-2.5 font-medium whitespace-nowrap">권한</th><th className="px-3 py-2.5 font-medium whitespace-nowrap">담당 그룹</th>
          <th className="px-3 py-2.5 font-medium whitespace-nowrap">마지막 접속</th><th className={ACTION_HEAD_CLASS} />
        </tr></thead>
        <tbody>
          {admins.map((admin) => {
            return <tr key={admin.userId} className="group border-t border-line hover:bg-panel-hover">
            <td data-card="full" className="px-3 py-2.5 font-medium">{adminLabel(admin)}</td>
            <td className="px-3 py-2.5 text-text-dim"><span className="block truncate">{admin.loginId ?? '-'}</span><span className="block truncate text-[12px]">{admin.email}</span></td>
            <td className="px-3 py-2.5">{roleLabel(admin.role)}</td>
            <td className="px-3 py-2.5 text-text-dim">{scopeOf(admin)}</td>
            <td className="px-3 py-2.5 whitespace-nowrap text-text-dim" title={formatDateTime(admin.lastSeenAt)}>{relativeTime(admin.lastSeenAt)}</td>
            <td className={ACTION_CELL_CLASS}>
              {/* 삭제는 수정 화면 맨 아래 위험 구역에 있다(2026-09-18 대교). */}
              {!write.hidden && <Button aria-label={`${adminLabel(admin)} 수정`} {...write.props} onClick={() => onEdit(admin)}>수정</Button>}
            </td>
          </tr>;
          })}
          {admins.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[14px] text-text-dim">관리자가 없습니다.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function DevicePanel({ title, devices, showGroup, trailing, render, empty, sort, onSort }: {
  title: string; devices: GroupDevice[]; showGroup: boolean; trailing?: JSX.Element | undefined;
  render: (device: GroupDevice) => JSX.Element; empty: string;
  sort: { key: DeviceSortKey; dir: 'asc' | 'desc' }; onSort: (key: DeviceSortKey) => void;
}): JSX.Element {
  const SortHead = ({ k, children }: { k: DeviceSortKey; children: ReactNode }) => (
    <button type="button" onClick={() => onSort(k)} aria-sort={sort.key === k ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn('inline-flex h-7 items-center gap-1 font-medium hover:text-text', sort.key === k && 'text-text')}>
      {children}<span aria-hidden="true" className="text-[12px]">{sort.key === k ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
    </button>
  );
  // 1280에서 열 머리가 낱자로 세로로 쪼개지고 행이 섹션 밖으로 넘치던 문제(S5):
  //   • 모든 th를 whitespace-nowrap — "할당된 타임라인"이 7줄 세로 낱자가 되지 않게.
  //   • 액션 열은 오른쪽 sticky — 가로 스크롤이 생겨도 수정·삭제는 늘 같은 자리에 보인다.
  //   • 스크롤 영역에 tabIndex/role=region — 가로로 더 있다는 단서이자 키보드로도 밀 수 있게.
  return (
    <Panel title={title} bodyClassName="p-0" trailing={trailing}>
      <div className="overflow-x-auto" tabIndex={0} role="region" aria-label={`${title} 표 — 가로로 스크롤할 수 있습니다`}>
        <table data-mobile-cards className="w-full text-left text-[14px]">
          <thead className="text-[13px] text-text-dim"><tr>
            <th className="px-3 py-2.5 font-medium whitespace-nowrap"><SortHead k="status">상태</SortHead> <InfoTooltip text={STATUS_TOOLTIP} /></th>
            <th className="px-3 py-2.5 font-medium whitespace-nowrap"><SortHead k="name">디바이스명</SortHead></th>
            <th className="px-3 py-2.5 font-medium whitespace-nowrap">운영 환경</th>{showGroup && <th className="px-3 py-2.5 font-medium whitespace-nowrap"><SortHead k="group">그룹</SortHead></th>}
            <th className="px-3 py-2.5 font-medium whitespace-nowrap">해상도</th><th className="px-3 py-2.5 font-medium whitespace-nowrap">할당된 타임라인</th><th className={ACTION_HEAD_CLASS} />
          </tr></thead>
          <tbody>
            {devices.map(render)}
            {devices.length === 0 && <tr><td colSpan={showGroup ? 8 : 7} className="px-3 py-8 text-center text-[14px] text-text-dim">{empty}</td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}
